import React from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { useModuleBEM, useAirPriceWatchStyles } from "@b2bportal/core-themes";
import { CoreAirPriceWatchComponent } from "@b2bportal/core-types";
import { Button } from "@b2bportal/core-ui";
import { FLIGHTS_SEARCH_PATH } from "@b2bportal/core-utilities";
// eslint-disable-next-line @nx/enforce-module-boundaries
import { trackEvent } from "@hopper-b2b/api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { WatchCard, WatchLoadingScreen } from "@tenant/air-price-watch";
import { NoTripResults } from "@tenant/trips";
import {
  AirPriceWatchRawEventName,
  useEmitCoreAirPriceWatchEventOnce,
} from "../../../../tracking";
import { events } from "../../../../types";
import type { WatchListProps } from "./types";
import defaultStyles from "./WatchList.module.scss";

const COMPONENT_KEY = CoreAirPriceWatchComponent.WatchList;

export const WatchList = (props: WatchListProps) => {
  const {
    watches,
    isLoading,
    hasError,
    refetch,
    emptyStateTitle,
    emptyStateSubtitle,
  } = props;

  useEmitCoreAirPriceWatchEventOnce(
    {
      name: AirPriceWatchRawEventName.AirPriceWatchesViewed,
      properties: {
        active_watches: watches?.watches.length ?? 0,
      },
    },
    !isLoading
  );

  // TODO Remove when all tenants have moved to core tracking
  React.useEffect(() => {
    trackEvent({
      eventName: events.VIEW_WATCHES,
      properties: {},
    });
  }, []);

  const styles = useAirPriceWatchStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const navigate = useNavigate();
  const { t } = useI18nContext();

  const handleGoToFlightSearch = () => {
    navigate(FLIGHTS_SEARCH_PATH);
  };

  if (isLoading) {
    return (
      <div className={block}>
        <WatchLoadingScreen isLoading />
      </div>
    );
  }

  return (
    <div className={block}>
      {watches && watches.watches.length > 0 ? (
        <>
          <ul className={cn("watch-list")}>
            {watches.watches.map((watch) => (
              <li className={cn("watch")} key={watch.id}>
                <WatchCard
                  watch={watch}
                  tripContext={watches.tripContext}
                  refetch={refetch}
                  showActions
                />
              </li>
            ))}
          </ul>
          <div className={cn("cta-container")}>
            <Button className={cn("cta")} onClick={handleGoToFlightSearch}>
              {t("flightSearchPrompt")}
            </Button>
          </div>
        </>
      ) : (
        <NoTripResults
          hasError={hasError}
          title={emptyStateTitle}
          subtitle={emptyStateSubtitle}
        />
      )}
    </div>
  );
};
